import React, { FC, useContext, useEffect, useState } from 'react';
import { Button, Form, Input, message, Select, Spin } from 'antd';
import { useRequest } from '@@/plugin-request/request';
import { listUser, TUserOptionDTO } from '@/services/UserRequest';
import 'antd/es/modal/style';
import 'antd/es/slider/style';
import styles from './index.less';
import { copyCamp, create, editCamp, info } from '@/services/CardCampRequest';
import ImageModuleUpload from '@/pages/backend/cardCampSettings/recruitPageSettings/ImageModuleUpload';
import { ExclamationCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { CardCampSettingContext } from '@/pages/backend/cardCampSettings/CardCampSettingContext';
import AdministratorsItem from '@/pages/backend/components/campForm/administratorsItem';
import { isCampNameRepeated } from '@/services/ConsoleRequest';
import { useModel } from '@@/plugin-model/useModel';
import cx from 'classnames';

const { TextArea } = Input;

type FieldType = {
  name: string;
  product: number[];
  ownerId?: number;
  ownerNameEn: string;
  ownerName?: string;
  administrators?: string;
  cover: string;
  info?: string;
};

export const productList = [
  {
    label: 'FineReport',
    value: '0',
  },
  {
    label: 'FineBI',
    value: '1',
  },
  {
    label: 'FineDataLink',
    value: '2',
  },
  {
    label: '简道云',
    value: '3',
  },
  {
    label: '九数云',
    value: '4',
  },
  {
    label: 'AI',
    value: '5',
  },
  {
    label: 'FineBI Next',
    value: '6',
  },
];

const getNameEn = (name: string): string => {
  return name?.substring(0, name?.indexOf('-'));
};

const CampForm: FC<{
  index?: number;
  isModal?: boolean;
  onOk?: (id?: string) => void;
  id?: string;
  onCancel?: () => void;
  isCopyMode?: boolean;
}> = (props) => {
  const { onFormChange } = useContext(CardCampSettingContext);
  const [form] = Form.useForm<FieldType>();
  const { initialState } = useModel('@@initialState');

  const [owners, setOwners] = useState<TUserOptionDTO[]>([]);
  const [basicInfo, setBasicInfo] = useState<any>();

  const { onCancel, id, onOk, index, isModal, isCopyMode } = props;

  const handleOwnerChange = (value: string) => {
    const o = owners.find((owner) => owner.nameEn === value);
    form.setFieldValue('ownerName', o?.nameEn + '-' + o?.nameZh);
  };

  const { run: infoRun } = useRequest(
    () => {
      return info(id as string);
    },
    {
      manual: true,
      onSuccess: (data) => {
        console.log('请求基本信息成功', data);
        const res = {
          ...data,
          product: data.product.split(','),
          ownerNameEn: getNameEn(data.ownerName),
        };
        setBasicInfo({ ...res });
        form.setFieldsValue({ ...res });
      },
    },
  );

  const { run: listOwnersRun } = useRequest(
    () => {
      return listUser();
    },
    {
      manual: true,
      onSuccess: (data) => {
        console.log('owner', data);
        setOwners(data as TUserOptionDTO[]);
      },
      onError: (e) => {
        message.error(e.message);
      },
    },
  );

  const { loading, run: createRun } = useRequest(
    (data) => {
      if (id) {
        if (isCopyMode) {
          return copyCamp(id, data);
        }
        return editCamp(id, data);
      }
      return create(data);
    },
    {
      manual: true,
      onSuccess: (res) => {
        console.log('创建 res是', res);
        if (id) {
          if (isCopyMode) {
            message.success('复制成功', 1, () => {
              infoRun();
              onOk?.(id);
            });
          } else {
            message.info('打卡营修改成功', 1, () => {
              infoRun();
              onOk?.();
            });
          }
        } else {
          message.info('打卡营创建成功');
          onOk?.(res.id);
        }
        form.resetFields();
        onFormChange?.(false);
      },
    },
  );

  const onFinish = async () => {
    const values = await form.validateFields();
    const calcIndex = () => {
      if (basicInfo) {
        if (isCopyMode) {
          return index || 0 - 1;
        }
        return basicInfo.index;
      } else {
        return index;
      }
    };
    const transferValues = {
      ...basicInfo,
      ...values,
      index: calcIndex(),
      product: values.product.join(','),
    };
    console.log('transferValues', transferValues);
    if (!isModal) {
      setBasicInfo(undefined);
      form.resetFields();
    }
    createRun(transferValues);
  };

  // 校验分组名称是否重复
  const checkNameRepeated = (_: any, value: string) => {
    return new Promise((resolve, reject) => {
      isCampNameRepeated({ name: value, id: isCopyMode ? undefined : id })
        .then((res) => {
          if (res.data) {
            reject(new Error('打卡营名称重复'));
          } else {
            resolve(res);
          }
        })
        .catch((error) => reject(error));
    });
  };

  useEffect(() => {
    listOwnersRun();
    if (id) {
      infoRun();
    }
  }, [id]);

  const calcVisibleAuth = () => {
    if (!id) {
      return initialState.permission?.superAdmin || initialState.permission?.createCamp;
    }
    return (
      initialState.permission?.superAdmin ||
      initialState.permission?.manageCamp ||
      initialState.nameEn === basicInfo?.ownerNameEn
    );
  };

  const formComponent = () => {
    return (
      <>
        <Form
          form={form}
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 10 }}
          initialValues={basicInfo ? { ...basicInfo } : undefined}
          autoComplete="off"
          onValuesChange={() => {
            onFormChange?.(true);
          }}
        >
          <Form.Item
            label="打卡营名称"
            name="name"
            validateTrigger={'onBlur'}
            validateFirst={true}
            rules={[
              { validator: checkNameRepeated },
              { required: true, message: '打卡营名称不能为空' },
            ]}
          >
            <Input showCount maxLength={50} />
          </Form.Item>

          <Form.Item
            label="产品"
            name="product"
            rules={[{ required: true, message: '产品不能为空' }]}
          >
            <Select
              mode="multiple"
              allowClear
              style={{ width: '100%' }}
              placeholder="请选择产品"
              options={productList}
            />
          </Form.Item>
          <Form.Item name="ownerName" hidden />
          {calcVisibleAuth() && (
            <Form.Item
              label="打卡营所有者"
              name="ownerNameEn"
              rules={[{ required: true, message: '打卡营所有者不能为空' }]}
            >
              <Select
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                //optionFilterProp="children"
                showSearch
                onChange={handleOwnerChange}
                options={owners.map((owner) => ({
                  key: owner?.nameEn,
                  value: owner?.nameEn,
                  label: `${owner?.nameEn}-${owner?.nameZh}`,
                }))}
              />
            </Form.Item>
          )}
          {calcVisibleAuth() && (
            <Form.Item label="共同管理员" name="administrators">
              <AdministratorsItem />
            </Form.Item>
          )}

          <Form.Item
            label="打卡营封面"
            name="cover"
            rules={[{ required: true, message: '打卡营封面不能为空' }]}
          >
            <ImageModuleUpload
              uploadButton={<PlusOutlined />}
              imageCrop={{ width: 572, height: 384 }}
              maxSize={0.5}
              uploadButtonSize={{ width: 572, height: 384 }}
              help="支持上传文件大小不超过 500 KB 的 .png\.jpg  格式图片，尺寸为572 * 384px"
            />
          </Form.Item>

          <Form.Item label="打卡营简介" name="info">
            <TextArea showCount maxLength={500} placeholder="请输入..." />
          </Form.Item>
        </Form>
      </>
    );
  };

  const renderForm = () => {
    if (id) {
      return basicInfo ? formComponent() : <Spin />;
    }
    return formComponent();
  };

  return (
    <div className={styles.top_container}>
      {isCopyMode && (
        <div className={styles.tips}>
          <ExclamationCircleOutlined style={{ color: '#0082fc' }} />
          除了批次外的所有信息都将复制，请填写复制后的打卡营基本信息
        </div>
      )}
      <div className={styles.top_container_body}>{renderForm()}</div>
      <div className={cx(styles.top_container_footer, isModal && styles.in_modal)}>
        {isModal && <Button onClick={onCancel}>取消</Button>}
        <Button loading={loading} type="primary" onClick={onFinish}>
          确认
        </Button>
      </div>
    </div>
  );
};

export default CampForm;
